class CollectionFilters {
  constructor() {
    // Configuration
    this.storefrontAccessToken = '76249782dd572514a4ca845b94d77d7d';
    this.shopDomain = window.Shopify?.shop || window.location.hostname;
    this.collectionHandle = document.querySelector('.collection-filters')?.dataset.collectionHandle;
    
    // Elements
    this.filterCheckboxes = document.querySelectorAll('.filter-checkbox');
    this.clearAllButton = document.getElementById('clear-all-filters');
    this.activeFiltersContainer = document.getElementById('active-filter-tags');
    this.productGrid = document.querySelector('.product-grid');
    this.loadingElement = document.querySelector('.collection-loading');
    this.sortSelect = document.querySelector('#SortBy');
    
    // State
    this.activeFilters = new Map();
    this.productTypeFilters = new Set();
    this.isLoading = false;
    
    // Model filtering
    this.showUniqueModelsOnly = document.querySelector('#main-collection-product-grid')?.dataset.uniqueModels === 'true';
    this.modelNameWordCount = parseInt(document.querySelector('#main-collection-product-grid')?.dataset.modelNameWordCount || '2');

    // Pagination state
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;
    this.infiniteScrollObserver = null;
    this.loadMoreTrigger = null;
    
    this.init();
  }
  
  init() {
    if (!this.collectionHandle) return;
    
    // Debug model filtering
    console.log('Model filtering:', this.showUniqueModelsOnly ? 'enabled' : 'disabled');
    console.log('Model word count:', this.modelNameWordCount);
    
    // Bind events
    this.filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleFilterChange.bind(this));
    });
    
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', this.clearAllFilters.bind(this));
    }

    const filterClear = document.querySelector('.filter-clear');
    if (filterClear) {
      filterClear.addEventListener('click', () => {
        this.toggleMobileFilters();
        this.clearAllFilters();
      });
    }
    
    // Mobile filter toggle
    const mobileFilterButton = document.querySelector('.filter-toggle-button');
    const filterClose = document.querySelector('.filter-close');
    const filterApply = document.querySelector('.filter-apply');
    
    if (mobileFilterButton) {
      mobileFilterButton.addEventListener('click', this.toggleMobileFilters);
    }
    
    if (filterClose) {
      filterClose.addEventListener('click', this.toggleMobileFilters);
    }

    if (filterApply) {
      filterApply.addEventListener('click', this.toggleMobileFilters);
    }

    const overlayLeft = document.querySelector('.content-overlay--left');
    if (overlayLeft) {
      overlayLeft.addEventListener('click', this.toggleMobileFilters);
    }

    // Sort select change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', this.handleSortChange.bind(this));
    }

    // Set up infinite scroll
    this.setupInfiniteScroll();
    
    // Initialize from URL params
    this.initFromUrl();
    
    // If we have URL params, perform initial filter
    if (this.hasActiveFilters() || this.productTypeFilters.size > 0) {
      this.fetchFilteredProducts();
    }
  }

  // Extract model name from product title
  extractModelName(title, wordCount = 2) {
    if (!title) return '';
    const words = title.trim().split(/\s+/);
    return words.slice(0, Math.min(wordCount, words.length)).join(' ').toLowerCase().trim();
  }

  setupInfiniteScroll() {
    // Create a trigger element for infinite scroll
    this.loadMoreTrigger = document.createElement('div');
    this.loadMoreTrigger.className = 'load-more-trigger';
    this.loadMoreTrigger.style.height = '1px';
    this.loadMoreTrigger.style.width = '100%';
    
    // Add the trigger to the end of the product grid
    if (this.productGrid) {
      this.productGrid.appendChild(this.loadMoreTrigger);
    }
    
    // Set up intersection observer
    this.infiniteScrollObserver = new IntersectionObserver((entries) => {
      // If trigger element is visible and we're not already loading
      if (entries[0].isIntersecting && !this.isLoading) {
        // Check if there are more pages to load
        if (this.currentPage < this.totalPages) {
          this.loadMoreProducts();
        }
      }
    }, {
      rootMargin: '0px 0px 100px 0px' // Load more when within 200px of the bottom
    });
    
    // Start observing the trigger
    if (this.loadMoreTrigger) {
      this.infiniteScrollObserver.observe(this.loadMoreTrigger);
    }
    
    // Get initial pagination info
    const paginationInfo = document.querySelector('.collection-pagination-info');
    if (paginationInfo) {
      this.currentPage = parseInt(paginationInfo.dataset.currentPage) || 1;
      this.totalPages = parseInt(paginationInfo.dataset.totalPages) || 1;
    }
  }

  async loadMoreProducts() {
    if (this.isLoading || this.currentPage >= this.totalPages) return;
    
    this.showLoading();
    
    try {
      // Build URL for next page
      let url = `/collections/${this.collectionHandle}`;
      
      // Add filter parameters as tags
      if (this.hasActiveFilters()) {
        const tagConstraints = [];
        
        this.activeFilters.forEach((values, category) => {
          values.forEach(value => {
            tagConstraints.push(`filter-${category}-${value}`);
          });
        });
        
        if (tagConstraints.length > 0) {
          url += `/${tagConstraints.join('+')}`;
        }
      }
      
      // Add view parameter and page number
      this.currentPage++;
      url += `?view=ajax&page=${this.currentPage}`;
      
      // Add sort parameter if present
      if (this.sortSelect?.value) {
        url += `&sort_by=${this.sortSelect.value}`;
      }
      
      // Fetch next page of products
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const html = await response.text();
      
      // Add new products to the grid
      if (this.productGrid) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Extract product elements
        let productElements = tempDiv.querySelectorAll('.card-product-wrapper');
        
        // Apply product type filter client-side if needed
        if (this.productTypeFilters.size > 0) {
          productElements = Array.from(productElements).filter(el => {
            const productType = el.getAttribute('data-product-type');
            return this.productTypeFilters.has(productType);
          });
        }
        
        // Apply OR logic within filter groups if needed
        if (this.hasActiveFilters()) {
          const filterGroups = {};
          this.activeFilters.forEach((values, category) => {
            filterGroups[category] = Array.from(values);
          });
          
          productElements = Array.from(productElements).filter(el => {
            const productTags = el.getAttribute('data-product-tags').split(',');
            
            // Product must match at least one value from each category
            for (const category in filterGroups) {
              const filterValues = filterGroups[category];
              const matchesAnyValueInCategory = filterValues.some(value => {
                const tagToMatch = `filter-${category}-${value}`;
                return productTags.includes(tagToMatch);
              });
              
              if (!matchesAnyValueInCategory) {
                return false;
              }
            }
            
            return true;
          });
        }
        
        // Apply model filtering if enabled
        if (this.showUniqueModelsOnly && productElements.length > 0) {
          const seenModels = new Set();
          
          // Get existing models already in the grid
          document.querySelectorAll('.card-product-wrapper').forEach(el => {
            const productTitle = 
              el.getAttribute('data-product-title') || 
              el.querySelector('.card-product__title')?.textContent || 
              '';
            
            if (productTitle) {
              const modelName = this.extractModelName(productTitle, this.modelNameWordCount);
              if (modelName) {
                seenModels.add(modelName);
              }
            }
          });
          
          // Filter new elements
          productElements = Array.from(productElements).filter(el => {
            const productTitle = 
              el.getAttribute('data-product-title') || 
              el.querySelector('.card-product__title')?.textContent || 
              '';
            
            const modelName = this.extractModelName(productTitle, this.modelNameWordCount);
            
            if (!seenModels.has(modelName) && modelName) {
              seenModels.add(modelName);
              return true;
            }
            return false;
          });
        }
        
        if (productElements.length === 0) {
          // No more products to load
          this.totalPages = this.currentPage - 1;
        } else {
          // Add products to grid (before the loadMoreTrigger)
          productElements.forEach(el => {
            this.productGrid.insertBefore(el.cloneNode(true), this.loadMoreTrigger);
          });
          
          // Initialize any required scripts for new product cards
          this.initProductCardScripts();
          
          // Update product count
          const currentCount = this.productGrid.querySelectorAll('.card-product-wrapper').length;
          this.updateProductCount(currentCount);
        }
      }
      
    } catch (error) {
      console.error('Error loading more products:', error);
      // Revert page number on error
      this.currentPage--;
    } finally {
      this.hideLoading();
    }
  }
  
  toggleMobileFilters() {
    document.body.classList.toggle('filters-drawer-open');
    const filtersElement = document.querySelector('.collection-filters');
    if (filtersElement) {
      filtersElement.classList.toggle('open');
    }
  }
  
  initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    
    // Handle each filter category
    const categories = ['color', 'shape', 'age-group', 'material', 'case-size', 'gender', 'frame-fit', 'strap-color', 'strap-material'];
    
    categories.forEach(category => {
      const values = params.getAll(category);
      if (values.length > 0) {
        // Create category Set if it doesn't exist
        if (!this.activeFilters.has(category)) {
          this.activeFilters.set(category, new Set());
        }
        
        // Add each value and check the corresponding checkbox
        values.forEach(value => {
          this.activeFilters.get(category).add(value);
          
          // Find checkbox with matching data attributes
          const checkbox = document.querySelector(`.filter-checkbox[data-category="${category}"][data-value="${value}"]`);
          if (checkbox) {
            checkbox.checked = true;
          }
        });
      }
    });

    // Handle product type filter
    const productTypes = params.getAll('product-type');
    if (productTypes.length > 0) {
      productTypes.forEach(type => {
        this.productTypeFilters.add(type);
        
        // Find checkbox with matching data attributes
        const checkbox = document.querySelector(`.filter-checkbox[data-category="product-type"][data-value="${type}"]`);
        if (checkbox) {
          checkbox.checked = true;
        }
      });
    }
    
    this.updateActiveFiltersUI();
  }
  
  hasActiveFilters() {
    return this.activeFilters.size > 0;
  }
  
  handleFilterChange(event) {
    const checkbox = event.target;
    const category = checkbox.dataset.category;
    const value = checkbox.dataset.value;
    
    if (!category || !value) return;
    
    // Find parent label and add/remove active class
    const label = checkbox.closest('.filter-option-label');

    // Handle product type filter
    if (category === 'product-type') {
      if (checkbox.checked) {
        if (label) label.classList.add('active');
        this.productTypeFilters.add(value);
      } else {
        if (label) label.classList.remove('active');
        this.productTypeFilters.delete(value);
      }
      
      this.updateActiveFiltersUI();
      this.updateURL();
      this.fetchFilteredProducts();
      return;
    }
    
    // Update active filters
    if (checkbox.checked) {
      // Add active class
      if (label) label.classList.add('active');
      
      // Create category Set if it doesn't exist
      if (!this.activeFilters.has(category)) {
        this.activeFilters.set(category, new Set());
      }
      this.activeFilters.get(category).add(value);
    } else {
      // Remove active class
      if (label) label.classList.remove('active');
      
      if (this.activeFilters.has(category)) {
        this.activeFilters.get(category).delete(value);
        if (this.activeFilters.get(category).size === 0) {
          this.activeFilters.delete(category);
        }
      }
    }
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }
  
  handleSortChange(event) {
    const sortValue = event.target.value;
    
    // Update URL with sort parameter
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    window.history.replaceState({}, '', url);
    
    // Fetch products with current filters and new sort
    this.fetchFilteredProducts();
  }
  
  updateActiveFiltersUI() {
    if (!this.activeFiltersContainer) return;
    
    this.activeFiltersContainer.innerHTML = '';
    
    // Create a fragment for better performance
    const fragment = document.createDocumentFragment();
    
    // Add a tag for each active filter
    this.activeFilters.forEach((values, category) => {
      values.forEach(value => {
        const filterTag = document.createElement('a');
        filterTag.className = 'active-filter-tag';
        filterTag.classList.add('button', 'button--secondary');
        filterTag.href = 'javascript:void(0)';
        filterTag.dataset.category = category;
        filterTag.dataset.value = value;
        
        // Format display text
        const displayCategory = category.replace(/-/g, ' ');
        const displayValue = value.replace(/-/g, ' ');
        
        filterTag.innerHTML = `<span class="active-filter-tag--title">${displayCategory.charAt(0).toUpperCase() + displayCategory.slice(1)}:</span> ${displayValue.charAt(0).toUpperCase() + displayValue.slice(1)}`;
        
        const closeIcon = document.createElement('span');
        closeIcon.className = 'filter-close-icon';
        closeIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
            <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="currentColor"/>
          </svg>`;
        
        filterTag.appendChild(closeIcon);
        
        filterTag.addEventListener('click', () => {
          this.removeFilter(category, value);
        });
        
        fragment.appendChild(filterTag);
      });
    });
    
    // Add product type filters
    this.productTypeFilters.forEach(type => {
      const filterTag = document.createElement('a');
      filterTag.className = 'active-filter-tag';
      filterTag.classList.add('button', 'button--secondary');
      filterTag.href = 'javascript:void(0)';
      filterTag.dataset.category = 'product-type';
      filterTag.dataset.value = type;
      
      filterTag.innerHTML = `<span class="active-filter-tag--title">Product Type:</span> ${type}`;
      
      const closeIcon = document.createElement('span');
      closeIcon.className = 'filter-close-icon';
      closeIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
          <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="currentColor"/>
        </svg>`;
      
      filterTag.appendChild(closeIcon);
      
      filterTag.addEventListener('click', () => {
        this.removeProductTypeFilter(type);
      });
      
      fragment.appendChild(filterTag);
    });
    
    this.activeFiltersContainer.appendChild(fragment);
    
    // Show/hide clear all button
    if (this.clearAllButton) {
      this.clearAllButton.style.display = (this.hasActiveFilters() || this.productTypeFilters.size > 0) ? 'block' : 'none';
    }
  }
  
  removeFilter(category, value) {
    // Uncheck corresponding checkbox
    const checkbox = document.querySelector(`.filter-checkbox[data-category="${category}"][data-value="${value}"]`);
    if (checkbox) {
      checkbox.checked = false;
    }
    
    // Remove from active filters
    if (this.activeFilters.has(category)) {
      this.activeFilters.get(category).delete(value);
      if (this.activeFilters.get(category).size === 0) {
        this.activeFilters.delete(category);
      }
    }
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }
  
  removeProductTypeFilter(type) {
    // Uncheck corresponding checkbox
    const checkbox = document.querySelector(`.filter-checkbox[data-category="product-type"][data-value="${type}"]`);
    if (checkbox) {
      checkbox.checked = false;
    }
    
    // Remove from product type filters
    this.productTypeFilters.delete(type);
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }
  
  clearAllFilters() {
    // Uncheck all checkboxes
    this.filterCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Clear active filters and product type filters
    this.activeFilters.clear();
    this.productTypeFilters.clear();
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }
  
  updateURL() {
    const url = new URL(window.location.href);
    
    // Clear existing filter params
    const categories = ['color', 'shape', 'age-group', 'material', 'case-size', 'gender', 'frame-fit', 'strap-color', 'strap-material', 'product-type'];
    categories.forEach(category => {
      url.searchParams.delete(category);
    });
    
    // Add current filters to URL
    this.activeFilters.forEach((values, category) => {
      values.forEach(value => {
        url.searchParams.append(category, value);
      });
    });

    // Add product type filters
    this.productTypeFilters.forEach(type => {
      url.searchParams.append('product-type', type);
    });
    
    // Preserve sort parameter
    const sortBy = this.sortSelect?.value;
    if (sortBy) {
      url.searchParams.set('sort_by', sortBy);
    }
    
    // Update URL without reloading page
    window.history.replaceState({}, '', url);
  }
  
  async fetchFilteredProducts() {
    if (!this.collectionHandle) return;
    this.currentPage = 1;
    
    this.showLoading();
    
    try {
      // Build URL for collection with filter parameters
      let url = `/collections/${this.collectionHandle}`;
      
      // Add tag-based filter parameters
      if (this.hasActiveFilters()) {
        const tagConstraints = [];
        
        this.activeFilters.forEach((values, category) => {
          values.forEach(value => {
            tagConstraints.push(`filter-${category}-${value}`);
          });
        });
        
        if (tagConstraints.length > 0) {
          url += `/${tagConstraints.join('+')}`;
        }
      }
      
      // Add view parameter for JSON response
      url += '?view=ajax';
      
      // Add sort parameter if present
      if (this.sortSelect?.value) {
        url += `&sort_by=${this.sortSelect.value}`;
      }
      
      // Fetch filtered collection HTML
      console.log('Fetching URL:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const html = await response.text();
      
      // Update product grid with filtered results
      if (this.productGrid) {
        // Clear existing products except loading element
        const loadingElement = this.productGrid.querySelector('.collection-loading');
        this.productGrid.innerHTML = '';
        if (loadingElement) {
          this.productGrid.appendChild(loadingElement);
        }
        
        // Add new products
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Extract product elements
        let productElements = tempDiv.querySelectorAll('.card-product-wrapper');
        console.log('Initial product count:', productElements.length);
        
        // Apply product type filter client-side if needed
        if (this.productTypeFilters.size > 0) {
          console.log('Applying product type filters:', Array.from(this.productTypeFilters));
          productElements = Array.from(productElements).filter(el => {
            const productType = el.getAttribute('data-product-type');
            console.log('Product type:', productType);
            return this.productTypeFilters.has(productType);
          });
          console.log('After product type filtering, count:', productElements.length);
        }
        
        // Apply OR logic within filter groups if needed
        if (this.hasActiveFilters()) {
          console.log('Applying OR logic within filter groups');
          const filterGroups = {};
          this.activeFilters.forEach((values, category) => {
            filterGroups[category] = Array.from(values);
          });
          
          productElements = Array.from(productElements).filter(el => {
            const productTags = el.getAttribute('data-product-tags').split(',');
            
            // Product must match at least one value from each category
            for (const category in filterGroups) {
              const filterValues = filterGroups[category];
              const matchesAnyValueInCategory = filterValues.some(value => {
                const tagToMatch = `filter-${category}-${value}`;
                return productTags.includes(tagToMatch);
              });
              
              if (!matchesAnyValueInCategory) {
                return false;
              }
            }
            
            return true;
          });
          console.log('After OR filtering, count:', productElements.length);
        }
        
        // Apply model filtering if enabled
        if (this.showUniqueModelsOnly && productElements.length > 0) {
          console.log('Applying model filtering, word count:', this.modelNameWordCount);
          const seenModels = new Set();
          productElements = Array.from(productElements).filter(el => {
            const productTitle = 
              el.getAttribute('data-product-title') || 
              el.querySelector('.card-product__title')?.textContent || 
              '';
            
            console.log('Product title:', productTitle);
            const modelName = this.extractModelName(productTitle, this.modelNameWordCount);
            console.log('Model name:', modelName);
            
            if (!seenModels.has(modelName) && modelName) {
              seenModels.add(modelName);
              return true;
            }
            return false;
          });
          console.log('After model filtering, count:', productElements.length);
        }
        
        if (productElements.length === 0) {
          // Show no results message
          const noResults = document.createElement('div');
          noResults.className = 'collection-no-results';
          noResults.innerHTML = `
            <h2 class="h3">No products found</h2>
            <p>Try adjusting your filters or <a class="link link-underline-start"href="/collections/${this.collectionHandle}">view all products</a>.</p>
          `;
          this.productGrid.appendChild(noResults);
        } else {
          // Add products to grid
          productElements.forEach(el => {
            this.productGrid.appendChild(el.cloneNode(true));
          });
          
          // Add load more trigger
          this.productGrid.appendChild(this.loadMoreTrigger);
          
          // Re-observe the trigger
          if (this.infiniteScrollObserver && this.loadMoreTrigger) {
            this.infiniteScrollObserver.observe(this.loadMoreTrigger);
          }
          
          // Initialize any required scripts for product cards
          this.initProductCardScripts();
        }
        
        // Update product count
        this.updateProductCount(productElements.length);
      }
      
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      
      // Show error message
      if (this.productGrid) {
        const errorMsg = document.createElement('div');
        errorMsg.className = 'collection-filter-error';
        errorMsg.innerHTML = `
          <h2>Error</h2>
          <p>Sorry, there was a problem filtering the products. Please try again.</p>
        `;
        
        // Clear grid and show error
        const loadingElement = this.productGrid.querySelector('.collection-loading');
        this.productGrid.innerHTML = '';
        if (loadingElement) {
          this.productGrid.appendChild(loadingElement);
        }
        this.productGrid.appendChild(errorMsg);
      }
    } finally {
      this.hideLoading();
      const paginationInfo = document.querySelector('.collection-pagination-info');
      if (paginationInfo) {
        this.totalPages = parseInt(paginationInfo.dataset.totalPages) || 1;
      }
    }
  }
  
  updateProductCount(count) {
    const countElement = document.getElementById('CollectionProductCount');
    if (countElement) {
      countElement.textContent = count === 1 
        ? '1 product' 
        : `${count} products`;
    }
  }
  
  initProductCardScripts() {
    // Initialize Swiper for product galleries
    if (window.Swiper) {
      const swipers = this.productGrid.querySelectorAll('.card-product__gallery.swiper');
      swipers.forEach(el => {
        new Swiper(el, {
          pagination: {
            el: el.querySelector('.swiper-pagination'),
            clickable: true
          }
        });
      });
    }
    
    // Initialize quick add buttons if needed
    if (window.initQuickAddButtons) {
      window.initQuickAddButtons(this.productGrid);
    }
  }
  
  showLoading() {
    this.isLoading = true;
    
    if (this.loadingElement) {
      this.loadingElement.style.display = 'flex';
    }
    
    // Add loading class to product grid
    if (this.productGrid) {
      this.productGrid.classList.add('loading');
    }
  }
  
  hideLoading() {
    this.isLoading = false;
    
    if (this.loadingElement) {
      this.loadingElement.style.display = 'none';
    }
    
    // Remove loading class from product grid
    if (this.productGrid) {
      this.productGrid.classList.remove('loading');
    }
  }
  
  formatMoney(cents) {
    const format = '€{{amount}}';
    
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision) {
      precision = precision || 2;
      number = (number / 100.0).toFixed(precision);
      return number.replace('.', ',');
    }
    
    switch(format.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2).replace(',', ' ');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    
    return format.replace(placeholderRegex, value);
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  new CollectionFilters();
});